import { QueryClient, QueryKey } from "@tanstack/react-query";
import moment from "moment";
import {
  AppointmentDto,
  EventTimeSlotDto,
  LeaveOfAbsenceDto,
  TimeSlotDto,
  AppointmentsOfAllTypesDto,
  ContinousAppLists,
  ContinuousAppointmentDto,
  WorkingHoursDto,
} from "../../api-client";
import { calendarKeys } from "./calendar.keys";
import { appointmentKeys } from "../Appointment/appointment.keys";

// Helper type to ensure items have defined IDs
type WithDefinedId<T> = T & { id: string };

// Type guard to check if an item has a defined ID
const hasDefinedId = <T extends { id?: string }>(item: T): item is WithDefinedId<T> => {
  return typeof item.id === "string" && item.id.length > 0;
};

// // ----- cache shape -----
// export type AppointmentsOfAllTypesDto = {
//   appointments?: AppointmentDto[];
//   eventTimeSlots?: EventTimeSlotDto[];
//   leaveOfAbsences?: LeaveOfAbsenceDto[];
//   goneFishings?: TimeSlotDto[];
//   travelTimes?: TimeSlotDto[];
//   lunchBreaks?: TimeSlotDto[];
//   // ...plus whatever else you're returning alongside the lists
// };

// keys in the object that point to lists
export type SectionKey = "appointments" | "eventTimeSlots" | "leaveOfAbsences" | "travelTimes";

export type SectionItem<K extends SectionKey> = K extends "appointments"
  ? AppointmentDto
  : K extends "eventTimeSlots"
  ? EventTimeSlotDto
  : K extends "leaveOfAbsences"
  ? LeaveOfAbsenceDto
  : K extends "travelTimes"
  ? TimeSlotDto
  : never;

// unified action type
export type CacheAction = "Create" | "Update" | "Delete";

// ----- last-issued-wins guard for concurrent reschedules -----
//
// The reschedule API may run on multiple instances with variable load, so two
// rapid moves of the SAME appointment can complete OUT OF ORDER. Without a guard
// the later-completing (but older) response overwrites the cache and visibly
// snaps the appointment back to its previous slot — the "jumping" seen when a
// misplacement is corrected by a second move.
//
// We stamp every optimistic mutation with a monotonic sequence number per
// appointment id. A mutation's settle handlers (onSuccess / onError) only touch
// the cache if its sequence is still the latest issued for that id; stale
// completions are ignored, because the newest move already owns the truth.
const appointmentMutationSeq = new Map<string, number>();
let appointmentMutationCounter = 0;

/** Record a new optimistic mutation for `id` and return its sequence number. */
export const beginAppointmentMutation = (id: string): number => {
  const seq = ++appointmentMutationCounter;
  appointmentMutationSeq.set(id, seq);
  return seq;
};

/** True if `seq` is still the most recently issued mutation for `id`. */
export const isLatestAppointmentMutation = (id: string, seq: number): boolean =>
  appointmentMutationSeq.get(id) === seq;

// ----- "this tab put it back" stamps (stale self-Delete guard) -----
//
// SocketHandler suppresses this tab's own echoes with ONE exception: Deletes are
// processed, because slot-level SPLIT/MERGE side effects delete appointments the
// HTTP response cannot enumerate. That exception rests on an assumption — that a
// Delete is still true by the time the buffered flush applies it, 5–20 s later.
//
// It is not always true, because a Delete broadcast does not always mean the row
// is gone. Cancelling an appointment emits one ("cancelled, so not shown in the
// calendar") while the appointment keeps its id, and re-planning it off the
// "Offene Termine" shelf is a normal next action. The mutation puts it back
// immediately; the stale Delete then lands and takes it away again — the same
// vanishing act as the split-appointment Delete, just with a different origin.
//
// So we record when this tab last asserted an appointment IS on the calendar.
// A self-originated Delete older than that assertion is stale and is dropped;
// one with no later assertion is applied as before. Other users' Deletes are
// never affected — their deletions are authoritative.
const appointmentAffirmedAt = new Map<string, number>();
/** Beyond this, a stamp can no longer outlive a buffered socket event. */
const AFFIRMED_RETENTION_MS = 120000;

/** Record that this tab just wrote `id` onto the calendar as a live appointment. */
export const affirmAppointmentAlive = (id: string | null | undefined): void => {
  if (!id) return;
  const now = Date.now();
  if (appointmentAffirmedAt.size > 500) {
    for (const [key, at] of appointmentAffirmedAt) {
      if (now - at > AFFIRMED_RETENTION_MS) appointmentAffirmedAt.delete(key);
    }
  }
  appointmentAffirmedAt.set(id, now);
};

/**
 * True if this tab re-established `id` at or after `since` (epoch ms).
 *
 * Ties count as "re-established": the broadcast and the HTTP response of one
 * operation routinely land in the same millisecond, and that is precisely the
 * case the guard exists for. When the order genuinely cannot be told, keeping
 * the appointment is the recoverable mistake — a stale one is corrected by the
 * next refetch, while a wrongly removed one loses what the user just planned.
 */
export const wasAppointmentAffirmedSince = (
  id: string | null | undefined,
  since: number
): boolean => !!id && (appointmentAffirmedAt.get(id) ?? 0) >= since;

/**
 * Should SocketHandler drop this buffered event instead of applying it?
 *
 * True only for THIS tab's own Delete of an appointment it has since put back
 * on the calendar. Everything else is applied: other users' deletions are
 * authoritative, non-Delete self-echoes are already filtered at enqueue, and a
 * self-Delete with no later placement (a real deletion, or an appointment
 * absorbed by a MERGE) still needs to reach the cache.
 *
 * `receivedAt` is when the event arrived, so the comparison asks the right
 * question: did the user plan this appointment back AFTER the server said it
 * had left the calendar?
 */
export const isStaleSelfDeleteEcho = (
  event: { action?: string; type?: string; clientId?: string; data?: { id?: string } },
  receivedAt: number,
  selfClientId: string
): boolean =>
  event.action === "Delete" &&
  !!event.clientId &&
  event.clientId === selfClientId &&
  (event.type === "Appointment" || event.type === "CancelledAppointment") &&
  wasAppointmentAffirmedSince(event.data?.id, receivedAt);

// ----- optimistic update helpers (onMutate / onError) -----

export type CalendarCacheSnapshot = [QueryKey, AppointmentsOfAllTypesDto | undefined][];

/**
 * Snapshot all cached appointmentsForDate days so a failed mutation can roll back.
 */
export const snapshotCalendarCaches = (queryClient: QueryClient): CalendarCacheSnapshot =>
  queryClient.getQueriesData<AppointmentsOfAllTypesDto>({
    queryKey: calendarKeys.appointmentsForDate.all(),
  });

export const restoreCalendarCaches = (
  queryClient: QueryClient,
  snapshot: CalendarCacheSnapshot
) => {
  snapshot.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
};

/**
 * Merge a provisional (field-level) update into a cached appointment.
 * Only start/end and the matching timeslots' window/user are taken from the
 * update payload — all other relations (therapyRx, patient, notes, ...) are
 * preserved from the cache. The canonical server DTO replaces this wholesale
 * in onSuccess.
 */
export const mergeProvisionalAppointment = (
  cached: AppointmentDto,
  update: AppointmentDto
): AppointmentDto => {
  const updatedSlotsById = new Map(
    (update.timeSlots ?? []).filter(hasDefinedId).map((slot) => [slot.id, slot])
  );
  return {
    ...cached,
    start: update.start ?? cached.start,
    end: update.end ?? cached.end,
    timeSlots: (cached.timeSlots ?? []).map((slot) => {
      const updated = slot.id ? updatedSlotsById.get(slot.id) : undefined;
      if (!updated) return slot;
      return {
        ...slot,
        start: updated.start ?? slot.start,
        end: updated.end ?? slot.end,
        // user.id drives resource/column placement; name fields are corrected by onSuccess
        user: updated.user?.id ? { ...slot.user, ...updated.user } : slot.user,
      };
    }),
  };
};

/**
 * Optimistically patch a section item across all cached appointmentsForDate days.
 *
 * - Cancels in-flight day refetches so stale responses cannot overwrite the patch.
 * - Returns a snapshot for rollback in onError.
 *
 * This keeps the query cache in agreement with FullCalendar's own optimistic
 * placement during the mutation in-flight window, so any re-derivation of the
 * controlled `events` array cannot snap the event back to its old position.
 */
export const optimisticCalendarPatch = async <K extends SectionKey>(
  queryClient: QueryClient,
  section: K,
  itemId: string,
  patch: (item: SectionItem<K>) => SectionItem<K>
): Promise<CalendarCacheSnapshot> => {
  await queryClient.cancelQueries({ queryKey: calendarKeys.appointmentsForDate.all() });
  const snapshot = snapshotCalendarCaches(queryClient);

  queryClient.setQueriesData(
    { queryKey: calendarKeys.appointmentsForDate.all() },
    (old: AppointmentsOfAllTypesDto | undefined) => {
      const list = old?.[section] as SectionItem<K>[] | undefined;
      if (!list?.some((item) => item.id === itemId)) return old;
      return {
        ...old,
        [section]: list.map((item) => (item.id === itemId ? patch(item) : item)),
      };
    }
  );

  return snapshot;
};

/**
 * Optimistically upsert an appointment across all cached appointmentsForDate days.
 * - If the appointment is already cached (drag/move/resize): field-level merge.
 * - If it is not cached yet (planning from the shelf): insert a provisional payload.
 *
 * For the shelf-insert case the bare `update` payload is render-incomplete: it is
 * fabricated by updateReceivedAppointment from the lean draggable data and carries
 * no `therapyRx.rx.patient`, so the calendar would briefly render the event without
 * a patient (empty title, missing-patient warning) until the server DTO lands in
 * onSuccess. When the caller knows the fully-hydrated source appointment (e.g. the
 * pinboard / "Offene Termine" entry, which still holds the patient + relations), it
 * can pass it as `insertBase`; we then merge the new placement (times/user) onto that
 * complete appointment so the optimistic insert is render-complete from the start.
 *
 * FullCalendar only renders events intersecting the visible range, so upserting
 * into every cached day is safe (same convention as the websocket handler).
 */
export const optimisticAppointmentUpsert = async (
  queryClient: QueryClient,
  update: AppointmentDto,
  insertBase?: AppointmentDto
): Promise<CalendarCacheSnapshot> => {
  await queryClient.cancelQueries({ queryKey: calendarKeys.appointmentsForDate.all() });
  const snapshot = snapshotCalendarCaches(queryClient);

  // Build the provisional row for the shelf-insert case once, outside setQueriesData,
  // so the same render-complete object is inserted into every cached day.
  const insertItem: AppointmentDto = insertBase
    ? {
        // Reuse the move/resize merge to apply the new times+user onto the fully
        // hydrated source appointment (slot ids match, so patient/therapyRx survive).
        ...mergeProvisionalAppointment(insertBase, update),
        // A freshly scheduled appointment is no longer a pending cancellation; clear
        // the cancellation marker so it does not optimistically render as cancelled.
        originalDate: undefined,
      }
    : update;

  // From this moment this tab asserts the appointment is on the calendar, so a
  // Delete broadcast issued BEFORE the drop (e.g. the cancellation this drop is
  // undoing) must no longer be able to take it away again.
  affirmAppointmentAlive(update.id);

  queryClient.setQueriesData(
    { queryKey: calendarKeys.appointmentsForDate.all() },
    (old: AppointmentsOfAllTypesDto | undefined) => {
      if (!old) return old;
      const appointments = old.appointments ?? [];
      const exists = appointments.some((a) => a.id === update.id);
      return {
        ...old,
        appointments: exists
          ? appointments.map((a) =>
              a.id === update.id
                ? // Rescheduling resolves the cancellation, so drop the marker here too —
                  // otherwise the event keeps rendering struck through until the refetch.
                  { ...mergeProvisionalAppointment(a, update), originalDate: undefined }
                : a
            )
          : [insertItem, ...appointments],
      };
    }
  );

  return snapshot;
};

/**
 * Optimistically patch a single timeslot's window/user inside whichever cached
 * appointment contains it (used by slot-level reschedules).
 */
export const optimisticTimeSlotPatch = async (
  queryClient: QueryClient,
  slotId: string,
  patch: { start?: string; end?: string; userId?: string }
): Promise<CalendarCacheSnapshot> => {
  await queryClient.cancelQueries({ queryKey: calendarKeys.appointmentsForDate.all() });
  const snapshot = snapshotCalendarCaches(queryClient);

  queryClient.setQueriesData(
    { queryKey: calendarKeys.appointmentsForDate.all() },
    (old: AppointmentsOfAllTypesDto | undefined) => {
      if (!old?.appointments?.some((a) => a.timeSlots?.some((ts) => ts.id === slotId))) {
        return old;
      }
      return {
        ...old,
        appointments: old.appointments.map((appointment) => {
          if (!appointment.timeSlots?.some((ts) => ts.id === slotId)) return appointment;
          return {
            ...appointment,
            timeSlots: appointment.timeSlots.map((slot) =>
              slot.id === slotId
                ? {
                    ...slot,
                    start: patch.start ?? slot.start,
                    end: patch.end ?? slot.end,
                    user: patch.userId ? { ...slot.user, id: patch.userId } : slot.user,
                  }
                : slot
            ),
          };
        }),
      };
    }
  );

  return snapshot;
};

/* ------------------------------------------------------------------------- *
 * SHELF caches ("Offene Termine": unscheduled + cancelled appointments)      *
 *                                                                           *
 * A slot scheduled FROM the shelf leaves the shelf in the same moment it     *
 * lands on the grid. Both are React Query caches, so the placement is only   *
 * atomic if the shelf is patched in the same optimistic pass as the day —    *
 * otherwise the card lingers (its `staleTime` is 2 minutes) and the slot is  *
 * on screen twice.                                                          *
 * ------------------------------------------------------------------------- */

const SHELF_CACHE_KEYS: QueryKey[] = [
  calendarKeys.unscheduledPatientAppointments.all(),
  appointmentKeys.cancelledAppointmentsByPatient.all(),
];

/** Cached shelf lists, snapshotted for rollback. Entries are of mixed shape. */
export type ShelfCacheSnapshot = [QueryKey, unknown][];

export const snapshotShelfCaches = (queryClient: QueryClient): ShelfCacheSnapshot =>
  SHELF_CACHE_KEYS.flatMap((key) => queryClient.getQueriesData({ queryKey: key }));

export const restoreShelfCaches = (
  queryClient: QueryClient,
  snapshot: ShelfCacheSnapshot
) => {
  snapshot.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
};

/**
 * The hydrated appointment holding `slotId`, found in the shelf caches.
 *
 * The drag payload built for a shelf grip is deliberately lean (no
 * `therapyRx.rx.patient`), so an optimistic insert built from it alone renders
 * a patient-less event. The shelf query still holds the complete appointment —
 * this is the slot-level equivalent of the `insertBase` that the caller hands
 * `useSaveAppointment` for whole-appointment shelf drops.
 *
 * The cancelled shelf wraps its appointment one level down (`{ appointment }`),
 * matching how EventShelfController flattens the two sections into one list.
 */
export const findShelfAppointmentBySlotId = (
  queryClient: QueryClient,
  slotId: string
): AppointmentDto | undefined => {
  const holdsSlot = (appointment: AppointmentDto | undefined) =>
    !!appointment?.timeSlots?.some((ts) => ts.id === slotId);

  for (const key of SHELF_CACHE_KEYS) {
    for (const [, data] of queryClient.getQueriesData({ queryKey: key })) {
      for (const entry of (data ?? []) as any[]) {
        if (holdsSlot(entry)) return entry as AppointmentDto;
        if (holdsSlot(entry?.appointment)) return entry.appointment as AppointmentDto;
      }
    }
  }
  return undefined;
};

/**
 * Optimistically drop a slot from the shelf lists. An entry left with no slots
 * is removed outright — the whole card has been planned away.
 */
const removeSlotFromShelfCaches = (queryClient: QueryClient, slotId: string) => {
  const withoutSlot = <T extends { timeSlots?: TimeSlotDto[] }>(entry: T): T | undefined => {
    const slots = entry.timeSlots ?? [];
    const remaining = slots.filter((ts) => ts.id !== slotId);
    if (remaining.length === slots.length) return entry;
    return remaining.length > 0 ? { ...entry, timeSlots: remaining } : undefined;
  };

  SHELF_CACHE_KEYS.forEach((key) => {
    queryClient.setQueriesData({ queryKey: key }, (old: unknown) => {
      if (!Array.isArray(old)) return old;
      let changed = false;
      const next = old
        .map((entry: any) => {
          // Unscheduled shelf: the appointment IS the entry.
          if (entry?.timeSlots) {
            const patched = withoutSlot(entry);
            if (patched !== entry) changed = true;
            return patched;
          }
          // Cancelled shelf: the appointment hangs under `appointment`.
          if (entry?.appointment?.timeSlots) {
            const patched = withoutSlot(entry.appointment);
            if (patched === entry.appointment) return entry;
            changed = true;
            return patched ? { ...entry, appointment: patched } : undefined;
          }
          return entry;
        })
        .filter(Boolean);
      return changed ? next : old;
    });
  });
};

/**
 * Optimistic placement for a SLOT-level schedule/reschedule — the slot-level
 * counterpart of `optimisticAppointmentUpsert`.
 *
 * Two cases, and the second is what `optimisticTimeSlotPatch` alone could never
 * cover: it patches the slot inside whichever cached appointment already holds
 * it, and an UNSCHEDULED appointment is in the shelf query, never in
 * `appointmentsForDate` — so scheduling from the shelf found nothing to patch
 * and the drop had no optimistic placement at all.
 *
 *  • Slot already on a cached day (a move) → patch its window/user in place.
 *  • Slot not on any cached day (from the shelf) → insert a PROVISIONAL
 *    appointment holding just that slot, built from the hydrated shelf entry so
 *    it renders complete (patient, therapy, Rx) from the first frame.
 *
 * The provisional carries a synthetic id: the server SPLITs the slot into a
 * brand-new appointment whose id the client cannot predict. It needs no
 * cleanup — `reconcileSurvivorAppointment` strips the survivor's slots from
 * every other cached appointment and drops the ones left empty, which is
 * exactly what the provisional becomes once the canonical DTO lands.
 */
export const optimisticSlotSchedule = async (
  queryClient: QueryClient,
  slotId: string,
  patch: { start: string; end: string; userId?: string },
  insertBase?: AppointmentDto
): Promise<{ day: CalendarCacheSnapshot; shelf: ShelfCacheSnapshot }> => {
  await queryClient.cancelQueries({ queryKey: calendarKeys.appointmentsForDate.all() });
  const daySnapshot = snapshotCalendarCaches(queryClient);
  const shelfSnapshot = snapshotShelfCaches(queryClient);

  const isCached = daySnapshot.some(([, day]) =>
    day?.appointments?.some((a) => a.timeSlots?.some((ts) => ts.id === slotId))
  );

  if (isCached) {
    await optimisticTimeSlotPatch(queryClient, slotId, patch);
    removeSlotFromShelfCaches(queryClient, slotId);
    return { day: daySnapshot, shelf: shelfSnapshot };
  }

  const base = insertBase ?? findShelfAppointmentBySlotId(queryClient, slotId);
  const movedSlot = base?.timeSlots?.find((ts) => ts.id === slotId);
  if (base && movedSlot) {
    const provisional: AppointmentDto = {
      ...base,
      id: `optimistic-slot-${slotId}`,
      start: patch.start,
      end: patch.end,
      // Scheduling out of a cancellation resolves it, so the provisional must
      // not render struck through (same rule as optimisticAppointmentUpsert).
      originalDate: undefined,
      timeSlots: [
        {
          ...movedSlot,
          start: patch.start,
          end: patch.end,
          user: patch.userId ? { ...movedSlot.user, id: patch.userId } : movedSlot.user,
        },
      ],
    };

    queryClient.setQueriesData(
      { queryKey: calendarKeys.appointmentsForDate.all() },
      (old: AppointmentsOfAllTypesDto | undefined) => {
        if (!old) return old;
        return { ...old, appointments: [provisional, ...(old.appointments ?? [])] };
      }
    );
  }

  removeSlotFromShelfCaches(queryClient, slotId);
  return { day: daySnapshot, shelf: shelfSnapshot };
};

/**
 * Optimistically remove a standalone timeslot (a "freetext" EventTimeSlot, or a
 * LeaveOfAbsence / TravelTime) from every cached calendar day right away, so the
 * deleted entry cannot be rebuilt back onto the calendar from a stale cache while the
 * delete request is in flight. FullCalendar's own `event.remove()` is transient — the
 * controlled events array is rebuilt from this cache, so without this patch the entry
 * reappears (and a second delete attempt then 404s because it's already gone server-side).
 *
 * The slot id is unique, so attempting the removal in the sections it doesn't belong to
 * is a no-op (each untouched day keeps its identity). Returns a snapshot for rollback if
 * the delete genuinely fails.
 */
export const optimisticTimeSlotDelete = async (
  queryClient: QueryClient,
  slotId: string
): Promise<CalendarCacheSnapshot> => {
  await queryClient.cancelQueries({ queryKey: calendarKeys.appointmentsForDate.all() });
  const snapshot = snapshotCalendarCaches(queryClient);

  applyCalendarCachePatch(
    queryClient,
    "eventTimeSlots",
    "Delete",
    { id: slotId } as SectionItem<"eventTimeSlots">
  );
  applyCalendarCachePatch(
    queryClient,
    "leaveOfAbsences",
    "Delete",
    { id: slotId } as SectionItem<"leaveOfAbsences">
  );
  applyCalendarCachePatch(
    queryClient,
    "travelTimes",
    "Delete",
    { id: slotId } as SectionItem<"travelTimes">
  );

  return snapshot;
};

/**
 * After a slot-level reschedule (which may SPLIT the slot off its appointment and
 * MERGE it into another), inject the survivor appointment and remove its slots
 * from any other cached appointment so no slot is rendered twice. Appointments
 * left without slots are dropped.
 */
export const reconcileSurvivorAppointment = (
  queryClient: QueryClient,
  survivor: AppointmentDto
) => {
  if (!hasDefinedId(survivor)) return;
  affirmAppointmentAlive(survivor.id);
  const survivorSlotIds = new Set(
    (survivor.timeSlots ?? []).map((ts) => ts.id).filter(Boolean) as string[]
  );

  queryClient.setQueriesData(
    { queryKey: calendarKeys.appointmentsForDate.all() },
    (old: AppointmentsOfAllTypesDto | undefined) => {
      if (!old) return old;
      const others = (old.appointments ?? [])
        .filter((a) => a.id !== survivor.id)
        .map((a) => {
          const slots = a.timeSlots ?? [];
          const remaining = slots.filter((ts) => !ts.id || !survivorSlotIds.has(ts.id));
          return remaining.length === slots.length ? a : { ...a, timeSlots: remaining };
        })
        .filter((a) => (a.timeSlots?.length ?? 0) > 0);
      return { ...old, appointments: [survivor, ...others] };
    }
  );
};

/* ------------------------------------------------------------------------- *
 * CONTINUOUS view caches (Dauertermin / continuous appointments)            *
 *                                                                           *
 * The continuous calendar is fed by two query scopes that both hold a       *
 * `ContinousAppLists` ({ appointments, eventTimeSlots }). The helpers below *
 * mirror the regular-appointment optimistic helpers so the continuous view  *
 * gets the same "no snap-back during the in-flight window" guarantees.      *
 * ------------------------------------------------------------------------- */

const CONTINUOUS_CACHE_KEYS: QueryKey[] = [
  calendarKeys.continuousAppointmentsByDay.all(),
  calendarKeys.continuousAppointmentsForUser.all(),
];

export type ContinuousCacheSnapshot = [QueryKey, ContinousAppLists | undefined][];

export const snapshotContinuousCaches = (
  queryClient: QueryClient
): ContinuousCacheSnapshot =>
  CONTINUOUS_CACHE_KEYS.flatMap((key) =>
    queryClient.getQueriesData<ContinousAppLists>({ queryKey: key })
  );

export const restoreContinuousCaches = (
  queryClient: QueryClient,
  snapshot: ContinuousCacheSnapshot
) => {
  snapshot.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
};

const cancelContinuousQueries = async (queryClient: QueryClient) => {
  await Promise.all(
    CONTINUOUS_CACHE_KEYS.map((key) => queryClient.cancelQueries({ queryKey: key }))
  );
};

const setContinuousQueriesData = (
  queryClient: QueryClient,
  updater: (old: ContinousAppLists | undefined) => ContinousAppLists | undefined
) => {
  CONTINUOUS_CACHE_KEYS.forEach((key) => {
    queryClient.setQueriesData<ContinousAppLists>({ queryKey: key }, updater);
  });
};

/**
 * Field-level merge for a continuous appointment (mirrors mergeProvisionalAppointment).
 * Only the windows/users that the update touches are applied; the canonical
 * server DTO replaces this wholesale once the mutation resolves.
 */
const mergeProvisionalContinuousAppointment = (
  cached: ContinuousAppointmentDto,
  update: ContinuousAppointmentDto
): ContinuousAppointmentDto => {
  const updatedSlotsById = new Map(
    (update.timeSlots ?? []).filter(hasDefinedId).map((slot) => [slot.id, slot])
  );
  return {
    ...cached,
    start: update.start ?? cached.start,
    end: update.end ?? cached.end,
    timeSlots: (cached.timeSlots ?? []).map((slot) => {
      const updated = slot.id ? updatedSlotsById.get(slot.id) : undefined;
      if (!updated) return slot;
      return {
        ...slot,
        start: updated.start ?? slot.start,
        end: updated.end ?? slot.end,
        user: updated.user?.id ? { ...slot.user, ...updated.user } : slot.user,
      };
    }),
  };
};

/**
 * Optimistically merge a continuous appointment move across both continuous caches.
 */
export const optimisticContinuousAppointmentUpsert = async (
  queryClient: QueryClient,
  update: ContinuousAppointmentDto
): Promise<ContinuousCacheSnapshot> => {
  await cancelContinuousQueries(queryClient);
  const snapshot = snapshotContinuousCaches(queryClient);

  setContinuousQueriesData(queryClient, (old) => {
    if (!old?.appointments?.some((a) => a.id === update.id)) return old;
    return {
      ...old,
      appointments: old.appointments.map((a) =>
        a.id === update.id ? mergeProvisionalContinuousAppointment(a, update) : a
      ),
    };
  });

  return snapshot;
};

/**
 * Optimistically patch a single continuous timeslot's window/user inside whichever
 * cached continuous appointment contains it (slot-level continuous reschedules).
 */
export const optimisticContinuousTimeSlotPatch = async (
  queryClient: QueryClient,
  slotId: string,
  patch: { start?: string; end?: string; userId?: string }
): Promise<ContinuousCacheSnapshot> => {
  await cancelContinuousQueries(queryClient);
  const snapshot = snapshotContinuousCaches(queryClient);

  setContinuousQueriesData(queryClient, (old) => {
    if (!old?.appointments?.some((a) => a.timeSlots?.some((ts) => ts.id === slotId))) {
      return old;
    }
    return {
      ...old,
      appointments: old.appointments.map((appointment) => {
        if (!appointment.timeSlots?.some((ts) => ts.id === slotId)) return appointment;
        return {
          ...appointment,
          timeSlots: appointment.timeSlots.map((slot) =>
            slot.id === slotId
              ? {
                  ...slot,
                  start: patch.start ?? slot.start,
                  end: patch.end ?? slot.end,
                  user: patch.userId ? { ...slot.user, id: patch.userId } : slot.user,
                }
              : slot
          ),
        };
      }),
    };
  });

  return snapshot;
};

/**
 * Optimistically patch a continuous event timeslot (break/event lane in the
 * continuous view) across both continuous caches.
 */
export const optimisticContinuousEventTimeSlotPatch = async (
  queryClient: QueryClient,
  id: string,
  patch: { start?: string; end?: string; userId?: string }
): Promise<ContinuousCacheSnapshot> => {
  await cancelContinuousQueries(queryClient);
  const snapshot = snapshotContinuousCaches(queryClient);

  setContinuousQueriesData(queryClient, (old) => {
    if (!old?.eventTimeSlots?.some((e) => e.id === id)) return old;
    return {
      ...old,
      eventTimeSlots: old.eventTimeSlots.map((e) =>
        e.id === id
          ? {
              ...e,
              start: patch.start ?? e.start,
              end: patch.end ?? e.end,
              user: patch.userId ? { ...e.user, id: patch.userId } : e.user,
            }
          : e
      ),
    };
  });

  return snapshot;
};

/**
 * Optimistically remove a continuous event timeslot (a "freetext" in the Dauertermin
 * view) from both continuous caches right away. The continuous delete emits no websocket
 * echo and the mutation otherwise leaves the cache untouched, so without this patch the
 * deleted entry is rebuilt back onto the calendar until a manual reload. Returns a
 * snapshot for rollback if the delete genuinely fails.
 */
export const optimisticContinuousEventTimeSlotDelete = async (
  queryClient: QueryClient,
  id: string
): Promise<ContinuousCacheSnapshot> => {
  await cancelContinuousQueries(queryClient);
  const snapshot = snapshotContinuousCaches(queryClient);

  setContinuousQueriesData(queryClient, (old) => {
    if (!old?.eventTimeSlots?.some((e) => e.id === id)) return old;
    return {
      ...old,
      eventTimeSlots: old.eventTimeSlots.filter((e) => e.id !== id),
    };
  });

  return snapshot;
};

/* ------------------------------------------------------------------------- *
 * WORKING HOURS / BREAK caches (lunch breaks & work-hour exceptions)        *
 *                                                                           *
 * Breaks live in the workingHours cache (standard view) and the             *
 * continuousWorkPlansForUsers cache (continuous view) as `WorkingHoursDto`  *
 * entries with `isBreak === true`. Patching their window optimistically     *
 * prevents the break from snapping back while the create/update exception   *
 * (or weekly-work-day) mutation is in flight.                               *
 * ------------------------------------------------------------------------- */

const WORKING_HOURS_CACHE_KEYS: QueryKey[] = [
  calendarKeys.workingHours.all(),
  calendarKeys.continuousWorkPlansForUsers.all(),
];

export type WorkingHoursCacheSnapshot = [QueryKey, WorkingHoursDto[] | undefined][];

export const snapshotWorkingHoursCaches = (
  queryClient: QueryClient
): WorkingHoursCacheSnapshot =>
  WORKING_HOURS_CACHE_KEYS.flatMap((key) =>
    queryClient.getQueriesData<WorkingHoursDto[]>({ queryKey: key })
  );

export const restoreWorkingHoursCaches = (
  queryClient: QueryClient,
  snapshot: WorkingHoursCacheSnapshot
) => {
  snapshot.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
};

export type BreakMatch = {
  slotId?: string;
  weeklyWorkDayId?: string | number;
  userId?: string;
  date?: string;
};

/**
 * Optimistically patch a break entry's window across the workingHours caches.
 *
 * Matching precedence (most specific first):
 *  1. weeklyWorkDayId — continuous lunch break tied to a weekly work day
 *  2. slotId          — break backed by an existing work-hour exception slot
 *  3. userId [+ date] — newly created exception (no slot id yet)
 */
export const optimisticBreakPatch = async (
  queryClient: QueryClient,
  match: BreakMatch,
  patch: { start: string; end: string }
): Promise<WorkingHoursCacheSnapshot> => {
  await Promise.all(
    WORKING_HOURS_CACHE_KEYS.map((key) => queryClient.cancelQueries({ queryKey: key }))
  );
  const snapshot = snapshotWorkingHoursCaches(queryClient);

  const matches = (wh: WorkingHoursDto): boolean => {
    if (!wh.isBreak) return false;
    if (match.weeklyWorkDayId != null) {
      return String(wh.weeklyWorkDay?.id) === String(match.weeklyWorkDayId);
    }
    if (match.slotId) return wh.slotId === match.slotId;
    // Fallback (newly created exception, no slot id yet): require a user match to
    // avoid patching unrelated breaks.
    if (!match.userId || wh.userId !== match.userId) return false;
    // Scope to the same calendar day by comparing the cached entry's start day,
    // which is robust regardless of how the cache's own `date` field is formatted.
    if (match.date) return moment(wh.start).format("YYYY-MM-DD") === match.date;
    return true;
  };

  WORKING_HOURS_CACHE_KEYS.forEach((key) => {
    queryClient.setQueriesData<WorkingHoursDto[]>({ queryKey: key }, (old) => {
      if (!old?.some(matches)) return old;
      return old.map((wh) =>
        matches(wh) ? { ...wh, start: patch.start, end: patch.end } : wh
      );
    });
  });

  return snapshot;
};

/**
 * Map a concrete timeslot's `type` to its section in AppointmentsOfAllTypesDto.
 */
export const sectionForTimeSlotType = (type?: string): SectionKey | undefined => {
  switch (type) {
    case "EventTimeSlot":
      return "eventTimeSlots";
    case "LeaveOfAbsence":
      return "leaveOfAbsences";
    case "TravelTime":
      return "travelTimes";
    default:
      return undefined;
  }
};

// ----- websocket / mutation cache patching -----

const DAY_KEY_FORMAT = "YYYY-MM-DD";

/**
 * The local calendar day (YYYY-MM-DD) an item belongs to, derived from its `start`.
 * Returns undefined when the start is missing or unparseable.
 */
const itemDayKey = (item: { start?: string }): string | undefined => {
  if (!item.start) return undefined;
  const m = moment(item.start);
  return m.isValid() ? m.format(DAY_KEY_FORMAT) : undefined;
};

/**
 * Whether an item's [start, end] span covers the given cached day, mirroring the
 * server's per-day query semantics (`DATE(start) <= day AND DATE(end) >= day`).
 *
 * Multi-day items (a vacation LeaveOfAbsence spans many days) legitimately live in
 * EVERY cached day of their span. Ownership must NOT be judged by the start day
 * alone: doing so made any Update broadcast evict a multi-day vacation from every
 * cached day except its first — the "vacation was visible a moment ago and suddenly
 * vanished" production bug.
 *
 * Returns undefined when the span cannot be determined (caller falls back to an
 * unconditional upsert so a valid item is never dropped).
 */
const itemCoversDay = (
  item: { start?: string; end?: string },
  dayKey: string
): boolean | undefined => {
  const startDay = itemDayKey(item);
  if (!startDay) return undefined;
  const end = item.end ? moment(item.end) : undefined;
  const endDay = end?.isValid() ? end.format(DAY_KEY_FORMAT) : startDay;
  return startDay <= dayKey && dayKey <= endDay;
};

/**
 * The YYYY-MM-DD a cached `appointmentsForDate` entry represents, read from its query
 * key (`[scope, { date, userIds? }]`). Each cache entry holds exactly one day, so this
 * lets us scope inserts to the day an item actually falls on.
 */
const dayKeyFromQueryKey = (queryKey: QueryKey): string | undefined => {
  const params = queryKey[1] as { date?: string } | undefined;
  return params?.date;
};

/**
 * Apply a single Create/Update/Delete to ONE cached day's AppointmentsOfAllTypesDto.
 *
 * Crucially, it returns the SAME object when the patch changes nothing for that day
 * (deleting or updating an item that isn't there, or inserting an item that belongs to
 * a different day). That referential stability is what stops every incoming websocket
 * message from re-rendering the whole calendar — React Query only notifies subscribers
 * when the cached value's identity actually changes.
 *
 * Create and Update are both an upsert-by-id, scoped to the days the item's
 * [start, end] span covers (a multi-day LeaveOfAbsence lives on every day it spans):
 *  - present + still covers this day  → merge in place (keep fields the payload omits)
 *  - present + moved away             → remove from this day (the covered days insert it)
 *  - absent  + covers this day        → prepend
 *  - absent  + covers other days only → no-op (same reference)
 * When the item's span can't be determined we fall back to an unconditional upsert so a
 * valid item is never dropped.
 */
const patchCalendarDay = <K extends SectionKey>(
  day: AppointmentsOfAllTypesDto | undefined,
  dayKey: string | undefined,
  section: K,
  action: CacheAction,
  item: WithDefinedId<SectionItem<K>>
): AppointmentsOfAllTypesDto | undefined => {
  // Only patch days that are actually cached; never materialise a phantom day.
  if (!day) return day;

  const list = (day[section] ?? []) as SectionItem<K>[];
  const index = list.findIndex((entry) => entry.id === item.id);

  if (action === "Delete") {
    if (index === -1) return day; // not on this day → nothing to do
    return { ...day, [section]: list.filter((_, i) => i !== index) };
  }

  // Create / Update → upsert by id, scoped to the days the item's span covers.
  const covers = dayKey ? itemCoversDay(item, dayKey) : undefined;
  // If the span or the cached day is unknown we can't scope safely, so treat it as
  // belonging here (unconditional upsert) rather than risk dropping a valid item.
  const belongsHere = covers === undefined || covers;

  if (index === -1) {
    if (!belongsHere) return day; // belongs to another day → leave this one untouched
    return { ...day, [section]: [item, ...list] };
  }

  // Already on this day but the item moved to a different day → drop it here.
  if (!belongsHere) {
    return { ...day, [section]: list.filter((_, i) => i !== index) };
  }

  const next = list.slice();
  next[index] = { ...next[index], ...item }; // merge: keep fields the payload omits
  return { ...day, [section]: next };
};

/**
 * Apply a Create/Update/Delete across the cached `appointmentsForDate` days.
 *
 * Walks the cached days explicitly (instead of `setQueriesData`) so each day is only
 * written back when `patchCalendarDay` actually changed it — untouched days (including
 * every day the item doesn't fall on) keep their identity and their subscribers don't
 * re-render. This is what keeps an open day quiet when other users edit other days.
 */
export const applyCalendarCachePatch = <K extends SectionKey>(
  queryClient: QueryClient,
  section: K,
  action: CacheAction,
  item: SectionItem<K>
): void => {
  if (!hasDefinedId(item)) {
    console.warn(`Calendar cache patch skipped: ${section} ${action} item has no id`, item);
    return;
  }

  const cachedDays = queryClient.getQueriesData<AppointmentsOfAllTypesDto>({
    queryKey: calendarKeys.appointmentsForDate.all(),
  });

  for (const [queryKey, day] of cachedDays) {
    const dayKey = dayKeyFromQueryKey(queryKey);
    const next = patchCalendarDay(day, dayKey, section, action, item);
    if (next !== day) {
      queryClient.setQueryData(queryKey, next);
    }
  }
};

/**
 * Update treatment note documentation in timeslot cache
 * Finds timeslot by treatmentId and updates wasDocumentedCount and the single treatment note
 */
export const updateTimeslotDocumentation = (
  treatmentId: string,
  action: "Create" | "Update" | "Delete",
  treatmentNoteData: any
) => {
  const updateFn = (old?: AppointmentsOfAllTypesDto): AppointmentsOfAllTypesDto | undefined => {
    if (!old) return old;

    const appointments = old.appointments?.map((appointment) => {
      if (!appointment.timeSlots) return appointment;

      const timeSlots = appointment.timeSlots.map((slot) => {
        if (slot.id === treatmentId) {
          // Found the timeslot - update its single treatment note
          if (action === "Create") {
            return {
              ...slot,
              wasDocumentedCount: 1,
              treatmentNote: treatmentNoteData,
            };
          } else if (action === "Update") {
            return {
              ...slot,
              treatmentNote: treatmentNoteData,
            };
          } else {
            // Delete
            return {
              ...slot,
              wasDocumentedCount: 0,
              treatmentNote: undefined,
            };
          }
        }
        return slot;
      });

      return {
        ...appointment,
        timeSlots,
      };
    });

    return {
      ...old,
      appointments,
    };
  };

  return updateFn;
};
